/**
 * When a status change owes a disposition, and what to say about it.
 *
 * Step 6, part 3 of 5, and it SEQUENCES BEFORE the API narrowing on purpose.
 *
 * The API is about to refuse `maintenance` and `retired` on an asset that is
 * deployed against a live job, with a 409 naming the job and both remedies. A
 * refusal arriving from a control that still offers the option is a capability
 * removal with no replacement — the office picks "In Maintenance" from a menu
 * that offered it and gets an error. So the menus stop offering it first, and
 * say the same thing the 409 will say.
 *
 * ─── THE RULE, and why the frontend can evaluate it ────────────────────────
 *
 * `GET /equipment` already returns everything this needs — `status`,
 * `currentJobId` and `linkedJobStatus`, the last being the status of the job the
 * asset points at. No API change is required for this PR, which is what lets it
 * merge ahead of the narrowing.
 *
 * An asset owes a disposition when it is DEPLOYED (`in_use` / `left_on_site`)
 * and the job it names is still LIVE. `reserved` is not deployed — that gear is
 * standing in the yard, booked days out — and a COMPLETED job is not live, so
 * gear left over from one can be sent to the shop freely.
 *
 * ─── BOTH REMEDIES, NAMED ──────────────────────────────────────────────────
 *
 * An office user hitting this at 6am needs to know what to do next, not just
 * that they cannot. There are exactly two paths that record where the gear went
 * and both already exist:
 *
 *   Return to a shop      POST /equipment/:id/return, which takes a destination
 *                         yard — the "it came back" disposition.
 *   Remove it from the job  the dispatch or field equipment picker — the "it was
 *                         never really on this job" correction.
 *
 * The message names the job as well, because "some asset is on some job" is not
 * actionable and the job number is the thing they will go and look at.
 *
 * ─── THE DEFENSIVE READ IS NOT DECORATION ──────────────────────────────────
 *
 * `currentJobId ?? current_job_id` and its siblings: this payload is the office
 * surface, which is camelCase on reads, and `Equipment` in EquipmentScreen.tsx
 * declares BOTH spellings for every one of these fields because the two
 * conventions already collide at these call sites. Reading one spelling only
 * would render a confident wrong answer — the asset would read as free and the
 * menu would offer a transition the API refuses. That is the #1 recurring bug
 * in this repo, so it is read both ways here, once, rather than at each caller.
 */

/** Job states meaning somebody is still working against this job. */
export const LIVE_JOB_STATUSES = ['scheduled', 'active', 'pending', 'on_hold'];

/** Statuses meaning the gear is out at a rig. `reserved` is NOT one of them. */
export const DEPLOYED_STATUSES = ['in_use', 'left_on_site'];

/** The statuses the API will refuse on a deployed asset. */
export const DISPOSITION_OWING_STATUSES = ['maintenance', 'retired'];

/** Job states meaning nobody is working against this job any more. */
export const FINISHED_JOB_STATUSES = ['completed', 'cancelled'];

/** The shape this needs, read either way. Anything wider is a caller's concern. */
export interface DispositionSubject {
  status?: string | null;
  currentJobId?: string | null;
  current_job_id?: string | null;
  linkedJobStatus?: string | null;
  linked_job_status?: string | null;
  reservedJobNumber?: string | null;
  reserved_job_number?: string | null;
  name?: string | null;
}

export function jobIdOf(e: DispositionSubject): string | null {
  return e.currentJobId ?? e.current_job_id ?? null;
}

export function jobStatusOf(e: DispositionSubject): string | null {
  return e.linkedJobStatus ?? e.linked_job_status ?? null;
}

export function jobNumberOf(e: DispositionSubject): string | null {
  return e.reservedJobNumber ?? e.reserved_job_number ?? null;
}

/** Out at a rig, on a job somebody is still working. */
export function isDeployedOnLiveJob(e: DispositionSubject): boolean {
  if (!DEPLOYED_STATUSES.includes(e.status ?? '')) return false;
  if (!jobIdOf(e)) return false;
  return LIVE_JOB_STATUSES.includes(jobStatusOf(e) ?? '');
}

/**
 * Out at a rig, on a job that has FINISHED — the backlog nobody could see.
 *
 * The API's `finished_job` class on GET /dispatch/equipment-alerts, mirrored so
 * the office equipment list can filter and count the same population without a
 * second request. `linkedJobStatus` already reached this client and was used for
 * exactly one thing: disabling two menu entries.
 *
 * ─── `in_use` ONLY, NOT `left_on_site` ─────────────────────────────────────
 *
 * Deliberate, and it is the one place this function departs from
 * `isDeployedOnLiveJob` above rather than being its negation.
 *
 * `left_on_site` is a positive claim somebody made — the gear is on a lease and
 * a hand said so — where this is the ABSENCE of a claim: the job ended and
 * nobody said where the tong went. They want different handling and the API
 * keeps them as separate alert classes for the same reason. Folding them here
 * would put 8 legitimately-on-a-lease assets into a list whose whole purpose is
 * "these need chasing".
 *
 * ─── AN UNKNOWN JOB STATUS IS NOT A FINISHED ONE ───────────────────────────
 *
 * A missing `linkedJobStatus` returns false rather than true. The asset may well
 * be owed a disposition, but claiming so on absent data would put rows in front
 * of a dispatcher that nothing can substantiate — the same rule as rendering an
 * absent value as `—` rather than as a plausible default, applied to a
 * predicate instead of to a number.
 */
export function isOnFinishedJob(e: DispositionSubject): boolean {
  if ((e.status ?? '') !== 'in_use') return false;
  if (!jobIdOf(e)) return false;
  return FINISHED_JOB_STATUSES.includes(jobStatusOf(e) ?? '');
}

/**
 * True when moving this asset to `toStatus` would owe a disposition the API is
 * about to refuse. Only `maintenance` and `retired` do — `available` is itself
 * a disposition ("it came back") and the API closes the row on it.
 */
export function owesDisposition(e: DispositionSubject, toStatus: string): boolean {
  return DISPOSITION_OWING_STATUSES.includes(toStatus) && isDeployedOnLiveJob(e);
}

/**
 * The sentence. Names the job and BOTH remedies.
 *
 * Pluralised from the count, and asserted at n = 1 AND n = 2 — the singular is
 * the branch that ships wrong, because fixtures tend to have several of things.
 * A job with no number degrades to "a live job" rather than rendering "on ",
 * per the rule that an absent value is never a plausible-looking default.
 */
export function dispositionMessage(blocked: DispositionSubject[]): string {
  if (blocked.length === 0) return '';

  const one = blocked.length === 1;
  const jobs = Array.from(new Set(blocked.map(jobNumberOf).filter(Boolean))) as string[];
  const where = jobs.length === 0
    ? (one ? 'a live job' : 'live jobs')
    : jobs.length === 1
      ? jobs[0]
      : `${jobs.slice(0, -1).join(', ')} and ${jobs[jobs.length - 1]}`;

  const subject = one
    ? `${blocked[0].name ?? 'This item'} is still out on ${where}`
    : `${blocked.length} items are still out on ${where}`;

  return `${subject}. Record where ${one ? 'it' : 'they'} went first — `
    + `return ${one ? 'it' : 'them'} to a shop, or remove ${one ? 'it' : 'them'} from the job.`;
}
